import { ErrorMessage } from '@hookform/error-message'
import Card from 'components/Card'
import Dropdown from 'components/Dropdown'
import TopUpLayout from 'components/Layouts/TopUpLayout'
import Loader from 'components/Loader'
import { CURRENCIES } from 'constants/index'
import currencyFormatter from 'currency-formatter'
import useAuth from 'hooks/useAuth'
import { CashInSubmitInterface } from 'interfaces'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useNavigate } from 'react-router-dom'
import { isBalanceEnough, isInputZero } from 'utils/inputValidations'

const CashIn = () => {
  const navigate = useNavigate()
  const auth = useAuth()
  const [loading, setLoading] = useState(false)
  const [currency, setCurrency] = useState<any>(CURRENCIES[0])
  const [balance] = useState(auth.bankBalance())

  useEffect(() => {
    if (!auth.isAuth()) navigate('/')
  }, [auth])

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    criteriaMode: 'all',
  })

  const onSubmit = async (data: CashInSubmitInterface) => {
    await handleCashIn(data)
  }

  const handleCashIn = async (data: CashInSubmitInterface) => {
    setLoading(true)
    await new Promise((resolve) => setTimeout(resolve, 1000))
    auth.setTopUpAmount(data.amount)
    setLoading(false)
    navigate('/topup/select-method')
  }

  const handleMax = () => {
    setValue('amount', balance)
  }

  const handleSelectCurrency = (currency) => {
    setCurrency(currency)
  }

  return (
    <TopUpLayout>
      <Card>
        <div className="p-6 border-b border-neutral-200">
          <h2 className="text-xl font-semibold text-neutral-800">Top Up Your Wallet</h2>
          <p className="text-sm text-neutral-500 mt-1">Add funds from your bank account</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          <div className="mb-6">
            <label className="label">Currency</label>
            <Dropdown
              name={'Select'}
              options={[CURRENCIES[0]]}
              selected={currency}
              setSelected={handleSelectCurrency}
            />
          </div>

          <div className="mb-6">
            <label className="label">Amount</label>
            <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-neutral-500 uppercase tracking-wide">
                  Available: {currencyFormatter.format(balance, {})} {currency.currency}
                </span>
                <button
                  type="button"
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 px-2 py-1 rounded-md hover:bg-primary-50 transition-colors"
                  onClick={handleMax}
                >
                  MAX
                </button>
              </div>
              <Controller
                control={control}
                name="amount"
                rules={{
                  required: 'Amount is required.',
                  pattern: {
                    value: /^[0-9.]*$/,
                    message: 'Amount cannot be a negative value.',
                  },
                  min: {
                    value: 1,
                    message: 'Minimum amount is 1.',
                  },
                  validate: {
                    zeroValueInput: (value) =>
                      !isInputZero(value) || 'Amount cannot be zero.',
                    isBalanceEnough: (value) =>
                      isBalanceEnough(balance, value) ||
                      "You don't have enough balance.",
                  },
                }}
                render={({ field }) => (
                  <NumberFormat
                    {...field}
                    decimalScale={2}
                    placeholder="0.00"
                    className="w-full bg-transparent text-3xl font-bold text-neutral-800 focus:outline-none"
                  />
                )}
              />
            </div>

            <div className="mt-2 min-h-[24px]">
              <ErrorMessage
                errors={errors}
                name="amount"
                render={({ messages }) =>
                  messages
                    ? Object.entries(messages).map(([type, message]) => (
                        <p className="text-sm text-error-600" key={type}>
                          {message}
                        </p>
                      ))
                    : null
                }
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary btn-lg w-full"
            disabled={loading}
          >
            {loading ? <Loader /> : 'Continue'}
          </button>
        </form>
      </Card>
    </TopUpLayout>
  )
}

export default CashIn
